<script setup>
  import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
  import connectionService from '../../services/ConnectionService'
  import BcsModal from '../BcsModal.vue'
  import { checkConnectionQuality } from './helpers'
  import { CONNECTION_TEXT_DEFINITIONS } from './constants'

  const props = defineProps({
    offlineText: {
      type: String,
      default: CONNECTION_TEXT_DEFINITIONS.connectionOfflineMessage.body
    },
    slowConnectionText: {
      type: String,
      default: CONNECTION_TEXT_DEFINITIONS.connectionSlowMessage.body
    },
    restoredText: {
      type: String,
      default: CONNECTION_TEXT_DEFINITIONS.connectionRestoredMessage.body
    },
    retryButtonText: {
      type: String,
      default: 'Retry Connection'
    },
    reloadButtonText: {
      type: String,
      default: 'Reload'
    },
    offlineModalTitle: {
      type: String,
      default: CONNECTION_TEXT_DEFINITIONS.connectionOfflineMessage.title
    },
    restoredModalTitle: {
      type: String,
      default: CONNECTION_TEXT_DEFINITIONS.connectionRestoredMessage.title
    },
    showSlowConnectionWarnings: {
      type: Boolean,
      default: true
    }
  })

  const modalRef = ref(null)
  const isOffline = ref(false)
  const connectionQuality = ref('good') // 'good', 'moderate', 'poor', 'offline', 'restored'
  const currentModalType = ref('warning')
  const currentMessage = ref(props.offlineText)

  const isRestoredState = computed(() => connectionQuality.value === 'restored')
  const currentModalTitle = computed(() => (
    isRestoredState.value ? props.restoredModalTitle : props.offlineModalTitle
  ))
  const currentButtonText = computed(() => (
    isRestoredState.value ? props.reloadButtonText : props.retryButtonText
  ))

  const pendingRestoredOpen = ref(false)

  const openRestoredModal = () => {
    connectionQuality.value = 'restored'
    currentModalType.value = 'success'
    currentMessage.value = props.restoredText
    isOffline.value = false

    nextTick(() => {
      modalRef.value?.openModal()
    })
  }

  const showConnectionRestoredModal = () => {
    // Close the blocking offline modal first so its static backdrop is removed
    // before reopening with dismissible restored content. The actual reopen is
    // deferred to handleCloseModal, which fires on Bootstrap's `hidden.bs.modal`,
    // so we never race with the hide transition/backdrop cleanup.
    if (connectionQuality.value === 'offline') {
      pendingRestoredOpen.value = true
      modalRef.value?.closeModal()
      return
    }

    openRestoredModal()
  }

  const updateModalForConnectionQuality = (quality) => {
    if (quality === 'offline') {
      currentModalType.value = 'warning'
      currentMessage.value = props.offlineText
      isOffline.value = true
      connectionQuality.value = 'offline'
    } else if (quality === 'poor' && props.showSlowConnectionWarnings) {
      currentModalType.value = 'info'
      currentMessage.value = props.slowConnectionText
      if (isOffline.value) {
        modalRef.value?.closeModal()
      }
      isOffline.value = false
      connectionQuality.value = quality
    } else {
      isOffline.value = false
      connectionQuality.value = quality
      modalRef.value?.closeModal()
      return
    }

    nextTick(() => {
      modalRef.value?.openModal()
    })
  }

  const evaluateSlowConnection = (quality) => {
    if (isOffline.value || isRestoredState.value) return
    if (quality === 'offline' || quality === 'error') return
    connectionQuality.value = quality
    updateModalForConnectionQuality(quality)
  }

  const handleConnectionChange = (online) => {
    if (!online) {
      connectionQuality.value = 'offline'
      updateModalForConnectionQuality('offline')
      return
    }

    if (isOffline.value || isRestoredState.value) {
      showConnectionRestoredModal()
      return
    }

    connectionQuality.value = 'good'
    modalRef.value?.closeModal()
  }

  const handleModalButtonClick = async () => {
    if (isRestoredState.value) {
      window.location.reload()
      return
    }

    const isOnline = await connectionService.checkActiveConnection()

    if (isOnline) {
      // reportConnectionRestored() only notifies listeners (closing/updating
      // this modal) on an offline->online transition. If the service already
      // considered itself online (e.g. this button belongs to the
      // slow-connection modal), that notification never fires, so update the
      // UI directly to avoid leaving the modal stuck open.
      const wasAlreadyOnline = connectionService.getConnectionStatus()
      connectionService.reportConnectionRestored()
      if (wasAlreadyOnline) {
        handleConnectionChange(true)
      }
      return
    }

    connectionQuality.value = 'offline'
    updateModalForConnectionQuality('offline')
  }

  const handleCloseModal = () => {
    if (pendingRestoredOpen.value) {
      pendingRestoredOpen.value = false
      openRestoredModal()
      return
    }

    if (isRestoredState.value) {
      connectionQuality.value = 'good'
    }
  }

  let connectionCheckInterval = null

  const startPeriodicConnectionCheck = () => {
    if (!props.showSlowConnectionWarnings) return

    if (connectionCheckInterval) {
      clearInterval(connectionCheckInterval)
    }

    connectionCheckInterval = setInterval(async () => {
      if (connectionService.getConnectionStatus()) {
        const { connectionQuality: quality } = await checkConnectionQuality()
        evaluateSlowConnection(quality)
      }
    }, 30000)
  }

  onMounted(() => {
    connectionService.registerConnectionListener(handleConnectionChange)

    connectionQuality.value = 'good'
    setTimeout(async () => {
      if (props.showSlowConnectionWarnings) {
        const { connectionQuality: quality } = await checkConnectionQuality()
        evaluateSlowConnection(quality)
      }
    }, 3000)

    startPeriodicConnectionCheck()
  })

  onUnmounted(() => {
    connectionService.unregisterConnectionListener(handleConnectionChange)

    if (connectionCheckInterval) {
      clearInterval(connectionCheckInterval)
      connectionCheckInterval = null
    }
  })
</script>

<template>
  <BcsModal
    @modalButtonClick="handleModalButtonClick"
    @closeButtonClicked="handleCloseModal"
    ref="modalRef"
    :elevated="true"
    :disableOutsideClick="connectionQuality === 'offline'"
    :hideCloseButton="connectionQuality === 'offline'"
    :modalSize="'small'"
    :modalType="currentModalType"
    :modalButton="currentButtonText"
    :modalTitle="currentModalTitle">
    <div class="connection-message">
      {{ currentMessage }}
    </div>
  </BcsModal>
</template>

<style scoped>
.connection-message {
  text-align: center;
  margin-bottom: 20px;
}
</style>
